import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useSearchLeads } from '../../../services/loanService';
import { LoanLead } from '../../../services/schemas/loanServiceSchemas';
import { Nav, Tab } from 'react-bootstrap';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { getLegalEntityType, registrationLabelFor } from '../../../utils/validation/legalEntityType';
import LeadNotes from './LeadNotes';
import EditLeadModal from './EditLeadModal';
import './ApplicationWorkspace.css';

/**
 * One captured field. Rendered even when empty -- the business needs to see
 * everything captured at lead creation, and an omitted row is indistinguishable
 * from a field that was never filled in.
 */
const Field: React.FC<{ label: string; value?: React.ReactNode; className?: string }> = ({
    label,
    value,
    className = 'col-md-6',
}) => {
    const isEmpty = value === undefined || value === null || value === '';
    return (
        <div className={className}>
            <label className="small text-muted mb-1">{label}</label>
            <div className={isEmpty ? 'fst-italic text-muted' : 'fw-semibold'}>
                {isEmpty ? 'Not provided' : value}
            </div>
        </div>
    );
};

// Same channel styling the leads table uses, so a lead reads the same in both places.
const CHANNEL_META: Record<string, { label: string; icon: string; bg: string; color: string }> = {
    ONLINE_CONTACT_FORM:     { label: 'Website',  icon: 'fa-globe',    bg: '#e3f2fd', color: '#1565c0' },
    ONLINE_APPLICATION_FORM: { label: 'Website',  icon: 'fa-globe',    bg: '#e8f5e9', color: '#2e7d32' },
    WHATSAPP:                { label: 'WhatsApp', icon: 'fa-whatsapp', bg: '#e8f5e9', color: '#128C7E' },
    FACEBOOK:                { label: 'Facebook', icon: 'fa-facebook', bg: '#e7ecf5', color: '#1877F2' },
    LMS:                     { label: 'Captured in the office', icon: 'fa-desktop', bg: '#f3e5f5', color: '#6a1b9a' },
    WEBSITE:                 { label: 'Website',  icon: 'fa-globe',    bg: '#e8f5e9', color: '#2e7d32' },
    PHONE:                   { label: 'Phone',    icon: 'fa-phone',    bg: '#fff3e0', color: '#e65100' },
    CAMPAIGN:                { label: 'Campaign', icon: 'fa-bullhorn', bg: '#fce4ec', color: '#ad1457' },
};

const REFERRAL_SOURCE_LABELS: Record<string, string> = {
    BROKER:          'Broker',
    EXISTING_CLIENT: 'Existing client',
    STAFF:           'Staff member',
    WALK_IN:         'Walk-in',
    ONLINE:          'Online',
    EVENT:           'Event',
    OTHER:           'Other',
};

// Business-facing wording for the stored codes, so the page never shows a raw code.
const REGISTRATION_NUMBER_TYPE_LABELS: Record<string, string> = {
  CIPC: 'CIPC',
  PRACTICE_NUMBER: 'Practice number',
  TRUST_NUMBER: 'Trust number',
  ID_NUMBER: 'ID number',
  OTHER: 'Other',
};

// Lead statuses as the leads list shows them.
const STATUS_META: Record<string, { label: string; badge: string }> = {
  NEW: { label: 'New', badge: 'bg-primary' },
  COMPLETED: { label: 'Completed', badge: 'bg-success' },
  REJECTED: { label: 'Rejected', badge: 'bg-danger' },
  EXPIRED: { label: 'Expired', badge: 'bg-secondary' },
  CANCELLED: { label: 'Cancelled', badge: 'bg-dark' },
};

// The website's funding option, captured as a code.
const INCOME_TYPE_LABELS: Record<string, string> = {
  PO: 'Purchase order',
  TENDER: 'Tender',
  PRODUCTS_AND_SERVICES: 'Products and services',
  OTHER: 'Other',
};

const PREFERRED_CHANNEL_LABELS: Record<string, string> = {
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  MOBILE: 'Phone call',
};

// A login id (Cognito UUID) rather than a name. Leads saved before names were recorded hold one.
const LOGIN_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
    <div className="d-flex align-items-center mb-4">
        <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
            <i className={`fa ${icon} text-primary`}></i>
        </div>
        <h5 className="mb-0 fw-bold">{title}</h5>
    </div>
);

const LeadDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [lead, setLead] = useState<LoanLead | null>(null);
    const [editing, setEditing] = useState(false);
    const currentUser = useSelector((state: RootState) => state.auth.user);

    // Try to get lead from location state first (passed from list)
    const leadFromState = location.state as { lead?: LoanLead };

    // Fallback when the page is opened by link or refreshed: search every status. It used to look
    // among NEW leads only, so a completed, rejected or expired lead showed "Lead Not Found".
    const { data: leadsPage, isLoading, error } = useSearchLeads(
        { page: 0, size: 1000 },
        { query: { enabled: !leadFromState?.lead } }
    );

    useEffect(() => {
        // First, check if lead was passed via state
        if (leadFromState?.lead) {
            setLead(leadFromState.lead);
        } 
        // Otherwise, find it in the fetched leads
        else if (leadsPage?.content) {
            const foundLead = leadsPage.content.find(l => l.id === id);
            if (foundLead) {
                setLead(foundLead);
            }
        }
    }, [id, leadsPage, leadFromState]);

    if (isLoading && !leadFromState?.lead) {
        return (
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body text-center py-5">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <h5 className="mb-2">Loading Lead Details</h5>
                            <p className="text-muted">Please wait while we fetch the lead information...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !lead) {
        return (
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body text-center py-5">
                            <i className="fa fa-exclamation-triangle fa-3x text-danger mb-3"></i>
                            <h5 className="mb-2">Lead Not Found</h5>
                            <p className="text-muted mb-4">
                                {error ? 'Unable to load lead details. Please try again.' : 'The requested lead could not be found.'}
                            </p>
                            <button
                                className="btn btn-primary"
                                onClick={() => navigate('/leads')}
                            >
                                <i className="fa fa-arrow-left me-2"></i>
                                Back to Leads
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const isPersonal = lead.loanType === 'PERSONAL';
    // Only a NEW lead can be edited: once converted the application holds the details.
    const canEdit = lead.status === 'NEW';
    const leadName = lead.registeredName
        || `${lead.firstName || ''} ${lead.lastName || ''}`.trim()
        || lead.customerReference
        || 'Lead';

    const handleSaved = (saved: LoanLead) => {
        setLead(saved);
        // The page prefers the lead passed in navigation state, so replace it with the saved one;
        // otherwise the refetch after saving would put the old details back on screen.
        navigate(location.pathname, { replace: true, state: { lead: saved } });
    };

    const status = STATUS_META[lead.status || ''] ?? { label: lead.status || 'Unknown', badge: 'bg-secondary' };

    const convert = () => {
        // Store lead data and navigate to loan application
        sessionStorage.setItem('leadData', JSON.stringify(lead));
        navigate('/loan-application/new');
    };

    const bannerButton = { fontSize: '0.9rem' } as const;

    // Who created or changed the lead, as a person. Older leads recorded the login id instead of a
    // name: it is your own id when it matches the signed-in user, and otherwise not a name we have.
    const personLabel = (value?: string) => {
        if (!value) return undefined;
        if (!LOGIN_ID.test(value)) return value === 'system' ? 'the system' : value;
        if (currentUser && (value === currentUser.id || value === currentUser.username)) {
            return currentUser.name || currentUser.username || 'you';
        }
        return 'a team member';
    };

    // What used to fill a long right-hand column, as one row of tiles under the banner.
    const tiles: { label: string; icon: string; value: React.ReactNode; detail?: React.ReactNode }[] = [
        {
            label: 'Requested Amount',
            icon: 'fa-money',
            value: lead.loanAmount
                ? <span className="text-primary">{formatCurrency(lead.loanAmount)}</span>
                : <span className="fst-italic text-muted fw-normal fs-6">Not provided</span>,
            detail: `${isPersonal ? 'Personal' : 'Business'} loan`
        },
        {
            label: 'Lead Source',
            icon: 'fa-share-alt',
            value: lead.channel ? (
                <span className="fs-6 d-inline-block text-wrap" style={{ color: (CHANNEL_META[lead.channel] ?? { color: undefined }).color }}>
                    <i className={`fa ${(CHANNEL_META[lead.channel] ?? { icon: 'fa-question-circle' }).icon} me-2`}></i>
                    {(CHANNEL_META[lead.channel] ?? { label: lead.channel.replace(/_/g, ' ') }).label}
                </span>
            ) : <span className="fst-italic text-muted fw-normal fs-6">Unknown</span>,
            // "Not a referral" is an answer, not a missing value.
            detail: lead.referralSource ? (
                <span title={lead.referralNotes || undefined}>
                    Referred by {lead.referralSource}
                    {lead.referralSourceType && ` (${REFERRAL_SOURCE_LABELS[lead.referralSourceType] ?? lead.referralSourceType.replace(/_/g, ' ')})`}
                </span>
            ) : 'Not a referral'
        },
        {
            label: 'Created',
            icon: 'fa-plus-circle',
            value: lead.createdDate ? formatDate(lead.createdDate) : <span className="fst-italic text-muted fw-normal fs-6">Unknown</span>,
            detail: lead.createdBy ? `by ${personLabel(lead.createdBy)}` : undefined
        },
        {
            label: 'Form / Product',
            icon: 'fa-file-text-o',
            value: lead.formName
                ? <span className="fs-6 d-inline-block text-wrap">{lead.formName}</span>
                : <span className="fst-italic text-muted fw-normal fs-6">Not recorded</span>,
            detail: (
                <>
                    {(lead.agentId || lead.branchId || lead.campaignId) && (
                        <div>Agent {lead.agentId || '-'} · Branch {lead.branchId || '-'} · Campaign {lead.campaignId || '-'}</div>
                    )}
                    {!lead.referralSource && lead.referralNotes && <div>{lead.referralNotes}</div>}
                </>
            )
        },
        {
            label: 'Last Updated',
            icon: 'fa-edit',
            value: lead.modifiedDate && lead.modifiedDate !== lead.createdDate
                ? formatDate(lead.modifiedDate)
                : <span className="fst-italic text-muted fw-normal fs-6">Not since created</span>,
            detail: lead.modifiedDate && lead.modifiedDate !== lead.createdDate && lead.modifiedBy ? `by ${personLabel(lead.modifiedBy)}` : undefined
        }
    ];

    return (
        <div>
            {/* Header - the green banner the other pages use, with the lead's actions in it so they
                are in reach without scrolling. */}
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item">
                                    <Link className="text-white" to="/"><i className="fa fa-home me-2"></i>Home</Link>
                                </li>
                                <li className="breadcrumb-item"><Link className="text-white" to="/leads">Leads</Link></li>
                                <li className="breadcrumb-item active text-white" aria-current="page">Lead Details</li>
                            </ol>
                        </div>
                    </div>
                    <div className="row align-items-center g-3">
                        <div className="col-lg-6">
                            <h1 className="fs-2 fw-bold mb-2 text-white text-break">{leadName}</h1>
                            <div className="d-flex flex-wrap align-items-center gap-2 text-white">
                                <span className={`badge ${status.badge} px-3 py-2 border border-light`}>{status.label}</span>
                                <span className="badge bg-light text-dark px-3 py-2">
                                    <i className={`fa ${isPersonal ? 'fa-user' : 'fa-briefcase'} me-1`}></i>
                                    {isPersonal ? 'Personal' : 'Business'} loan
                                </span>
                                {lead.customerReference && (
                                    <span className="opacity-75"><i className="fa fa-hashtag me-1"></i>{lead.customerReference}</span>
                                )}
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
                                {/* Only a NEW lead can be edited or converted: the rest already were, or are closed. */}
                                {lead.status === 'NEW' && (
                                    <button
                                        type="button"
                                        className="btn btn-light shadow-lg px-4 py-2 rounded-pill fw-semibold text-success"
                                        style={bannerButton}
                                        onClick={convert}
                                    >
                                        <i className="fa fa-file-text me-2"></i>Convert to Application
                                    </button>
                                )}
                                {canEdit && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-light px-4 py-2 rounded-pill fw-semibold"
                                        style={bannerButton}
                                        onClick={() => setEditing(true)}
                                    >
                                        <i className="fa fa-pencil me-2"></i>Edit Lead
                                    </button>
                                )}
                                {lead.primaryPhone && (
                                    <a
                                        href={`tel:${lead.primaryPhone}`}
                                        className="btn btn-outline-light px-3 py-2 rounded-pill fw-semibold"
                                        style={bannerButton}
                                        title={`Call ${lead.primaryPhone}`}
                                    >
                                        <i className="fa fa-phone me-2"></i>Call
                                    </a>
                                )}
                                {lead.email && (
                                    <a
                                        href={`mailto:${lead.email}`}
                                        className="btn btn-outline-light px-3 py-2 rounded-pill fw-semibold"
                                        style={bannerButton}
                                        title={`Email ${lead.email}`}
                                    >
                                        <i className="fa fa-envelope me-2"></i>Email
                                    </a>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-outline-light px-3 py-2 rounded-pill"
                                    style={bannerButton}
                                    onClick={() => navigate('/leads')}
                                    title="Back to Leads"
                                >
                                    <i className="fa fa-arrow-left"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    {lead.status === 'CANCELLED' && (
                        <div className="alert alert-secondary d-flex gap-3 align-items-start mb-4" role="status">
                            <i className="fa fa-ban fs-4 mt-1"></i>
                            <div>
                                <div className="fw-semibold">
                                    This lead was cancelled{lead.cancelledBy ? ` by ${personLabel(lead.cancelledBy)}` : ''}
                                    {lead.cancelledAt ? ` on ${formatDate(lead.cancelledAt)}` : ''}.
                                </div>
                                {lead.cancellationReason && <div className="mt-1">Reason: {lead.cancellationReason}</div>}
                                <small className="text-muted">Cancelled leads are kept for the record and can still be found on the Leads page.</small>
                            </div>
                        </div>
                    )}
                    {/* Summary tiles */}
                    <div className="row g-3 mb-4">
                        {tiles.map(tile => (
                            <div key={tile.label} className="col-xl-3 col-md-6">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body p-3 d-flex align-items-start">
                                        <div className="rounded-circle p-2 me-3 flex-shrink-0" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                                            <i className={`fa ${tile.icon} text-primary`} style={{ width: 16, textAlign: 'center' }}></i>
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div className="small text-muted">{tile.label}</div>
                                            <div className="fs-5 fw-bold">{tile.value}</div>
                                            {tile.detail && <small className="text-muted d-block">{tile.detail}</small>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row g-4">
                        <div className="col-12">
                            {/* A personal lead has no company, so it opens on the person. */}
                            <Tab.Container key={isPersonal ? 'personal' : 'business'} defaultActiveKey={isPersonal ? 'person' : 'company'}>
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-body pb-0 pt-3 px-3">
                                        <Nav variant="tabs" className="flex-wrap application-workspace-tabs">
                                            {!isPersonal && (
                                                <Nav.Item>
                                                    <Nav.Link eventKey="company">
                                                        <i className="fa fa-building me-2"></i>Company
                                                    </Nav.Link>
                                                </Nav.Item>
                                            )}
                                            <Nav.Item>
                                                <Nav.Link eventKey="person">
                                                    <i className="fa fa-user me-2"></i>{isPersonal ? 'Customer' : 'Contact Person'}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="contact">
                                                    <i className="fa fa-phone me-2"></i>Contact Details
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="financial">
                                                    <i className="fa fa-bank me-2"></i>Financial &amp; Compliance
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="comments">
                                                    <i className="fa fa-sticky-note me-2"></i>Comments
                                                </Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    </div>
                                </div>

                                <Tab.Content>
                                    <Tab.Pane eventKey="company">
                                        {/* Company Information -- always rendered. Previously the whole
                                            card was hidden when registeredName was empty, which also hid
                                            the registration and customer numbers on individual leads. */}
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-body p-4">
                                                <SectionHeader icon="fa-building" title="Company Information" />
                                                <div className="row g-3">
                                                    <Field label="Company Name" value={lead.registeredName} />
                                                    <Field label="Trading Name" value={lead.tradingName} />
                                                    <Field label="Legal Entity Type" value={getLegalEntityType(lead.legalEntityType)?.label ?? lead.legalEntityType} />
                                                    <Field
                                                        label={`${lead.legalEntityType ? registrationLabelFor(lead.legalEntityType) : 'Registration Number'}${lead.registrationNumberType ? ` (${REGISTRATION_NUMBER_TYPE_LABELS[lead.registrationNumberType] ?? lead.registrationNumberType})` : ''}`}
                                                        value={lead.registrationNumber}
                                                    />
                                                    <Field label="Industry Sector" value={lead.industrySector} />
                                                    <Field label="Establishment Date" value={lead.establishmentDate} />
                                                    <Field
                                                        label="Number of Employees"
                                                        value={lead.employeeCount != null ? lead.employeeCount.toLocaleString() : undefined}
                                                    />
                                                    <Field label="Physical Address" value={lead.physicalAddress} className="col-12" />
                                                </div>
                                            </div>
                                        </div>
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="person">
                                        {/* Contact Person */}
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-body p-4">
                                                <SectionHeader icon="fa-user" title={isPersonal ? 'Customer' : 'Contact Person'} />
                                                <div className="row g-3">
                                                    <Field label="First Name" value={lead.firstName} />
                                                    <Field label="Last Name" value={lead.lastName} />
                                                    <Field label="ID Number" value={lead.idNumber} />
                                                    <Field label={isPersonal ? 'Occupation' : 'Role in the Business'} value={lead.representativeOccupation} />
                                                    {/* A director's designation and share only mean something for a business. */}
                                                    {!isPersonal && (
                                                        <>
                                                            <Field label="Designation" value={lead.designation} />
                                                            <Field
                                                                label="Ownership Percentage"
                                                                value={lead.ownershipPercentage != null ? `${lead.ownershipPercentage}%` : undefined}
                                                            />
                                                        </>
                                                    )}
                                                    {isPersonal && <Field label="Home Address" value={lead.physicalAddress} className="col-12" />}
                                                </div>
                                            </div>
                                        </div>
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="contact">
                                        {/* Contact Details */}
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-body p-4">
                                                <SectionHeader icon="fa-phone" title="Contact Details" />
                                                <div className="row g-3">
                                                    <Field
                                                        label="Phone Number"
                                                        value={lead.primaryPhone ? (
                                                            <a href={`tel:${lead.primaryPhone}`} className="text-decoration-none">
                                                                <i className="fa fa-phone me-2 text-success"></i>
                                                                {lead.primaryPhone}
                                                            </a>
                                                        ) : undefined}
                                                    />
                                                    <Field
                                                        label="Email Address"
                                                        value={lead.email ? (
                                                            <a href={`mailto:${lead.email}`} className="text-decoration-none">
                                                                <i className="fa fa-envelope me-2 text-info"></i>
                                                                {lead.email}
                                                            </a>
                                                        ) : undefined}
                                                    />
                                                    <Field
                                                        label="Preferred Channel"
                                                        value={lead.preferredChannel ? (
                                                            <span className="badge bg-light text-dark border">
                                                                <i className={`fa ${
                                                                    lead.preferredChannel === 'EMAIL' ? 'fa-envelope' :
                                                                    lead.preferredChannel === 'WHATSAPP' ? 'fa-whatsapp' :
                                                                    'fa-comment'
                                                                } me-1`}></i>
                                                                {PREFERRED_CHANNEL_LABELS[lead.preferredChannel] ?? lead.preferredChannel}
                                                            </span>
                                                        ) : undefined}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="financial">
                                        {/* Financial & Compliance */}
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-body p-4">
                                                <SectionHeader icon="fa-bank" title="Financial &amp; Compliance" />
                                                <div className="row g-3">
                                                    {!isPersonal && (
                                                        <Field
                                                            label="Average Monthly Turnover"
                                                            value={lead.turnover != null ? formatCurrency(lead.turnover) : undefined}
                                                        />
                                                    )}
                                                    <Field label={isPersonal ? 'Bank' : 'Primary Business Bank'} value={lead.bankName} />
                                                    {!isPersonal && (
                                                        <>
                                                            <Field
                                                                label="Source of Income"
                                                                value={lead.incomeType ? (INCOME_TYPE_LABELS[lead.incomeType] ?? lead.incomeType) : undefined}
                                                            />
                                                            <Field label="VAT Number" value={lead.vatNumber} />
                                                        </>
                                                    )}
                                                    <Field label="Tax Number" value={lead.taxNumber} />
                                                </div>
                                            </div>
                                        </div>
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="comments">
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-body p-4">
                                                <LeadNotes leadId={lead.id || ''} />
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        </div>
                    </div>
                </div>
            </div>

            <EditLeadModal
                lead={editing ? lead : null}
                onHide={() => setEditing(false)}
                onSaved={handleSaved}
            />
        </div>
    );
};

export default LeadDetails;

